<template>
  <component
    class="icon-btn flex w-fit flex-col items-center justify-center gap-spacing_1 rounded-r3 p-spacing_3 text-size_4 disabled:cursor-not-allowed"
    :disabled
    :is="link && !link.download ? nuxtLink : 'button'"
    :to="link ? link.path : undefined"
    :external="link?.external"
    :target="link?.external ? '_blank' : undefined"
    :class="[
      c_style?.name ?? 'main',
      c_style?.type ?? 'primary',
      c_style?.style ?? 'A',
    ]"
    @click="handleClick"
  >
    <template v-if="!loading">
      <slot>
        <UIcons
          v-if="name"
          class="h-icon_size_5 w-icon_size_5 md:h-icon_size_6 md:w-icon_size_6"
          :name
        />
      </slot>

      <p v-if="label">{{ label }}</p>
    </template>

    <slot v-else name="loading">
      <UIcons
        class="h-icon_size_5 w-icon_size_5 md:h-icon_size_6 md:w-icon_size_6"
        name="ic-spinner-loading"
        prefix="a"
      />
    </slot>
  </component>
</template>

<script setup lang="ts">
import { downloadFileInBrowser } from "~/utils/common/file";
import UIcons from "../UIcons.vue";
import type { IconButtonProps } from "~/types/schemas/common/Atom/iconButton";

const props = defineProps<IconButtonProps>();

const nuxtLink = resolveComponent("NuxtLink");

const handleClick = async () => {
  if (props.link?.path && props.link?.download) {
    await downloadFileInBrowser(
      props.link.path.toString(),
      props.link.downloadFileName,
    );
  }
};
</script>

<style scoped>
.icon-btn {
  &.main {
    &.primary {
      @apply bg-action text-action_text;

      &:hover {
        @apply bg-primary-700 text-neutral-100;
      }

      &:active {
        @apply bg-primary-800;
      }

      &:disabled {
        @apply bg-primary-200;
      }
    }

    &.secondary {
      &.A {
        @apply bg-primary-50 text-action;

        &:hover {
          @apply text-primary-700;
        }

        &:active {
          @apply bg-primary-100 text-primary-700;
        }

        &:disabled {
          @apply text-primary-200;
        }
      }

      &.B {
        @apply bg-neutral-50 text-action;

        &:hover {
          @apply text-primary-700;
        }

        &:active {
          @apply bg-neutral-400;
        }

        &:disabled {
          @apply text-primary-200;
        }
      }

      &.C {
        @apply bg-neutral-50 text-neutral-900;

        &:hover {
          @apply bg-neutral-300;
        }

        &:active {
          @apply bg-neutral-400;
        }

        &:disabled {
          @apply bg-neutral-50 text-neutral-500;
        }
      }
    }

    &.outline {
      &.A {
        @apply text-neutral-700 outline-[0.5px] outline-neutral-700;

        &:hover {
          @apply text-neutral-800 outline-neutral-800;
        }

        &:active {
          @apply text-neutral-800 outline-neutral-800;
        }

        &:disabled {
          @apply text-neutral-500 outline-neutral-500;
        }
      }

      &.B {
        @apply text-neutral-700 outline-[0.5px] outline-neutral-700;

        &:hover {
          @apply text-neutral-800 outline-neutral-800;
        }

        &:active {
          @apply text-neutral-800 outline-neutral-800;
        }

        &:disabled {
          @apply text-neutral-500 outline-neutral-500;
        }
      }
    }

    &.tertiary {
      @apply font-medium text-action;

      &:hover {
        @apply font-semibold;
      }

      &:active {
        @apply font-semibold text-primary-700;
      }

      &:disabled {
        @apply text-primary-200;
      }
    }

    &.ghost {
      @apply text-neutral-800;

      &:hover {
        @apply font-semibold;
      }

      &:active {
        @apply font-semibold text-neutral-700;
      }

      &:disabled {
        @apply text-neutral-500;
      }
    }
  }

  &.danger {
    &.icon {
      @apply border border-errors text-errors;

      &:hover {
        @apply font-semibold;
      }

      &:active {
        @apply font-semibold text-error-400;
      }

      &:disabled {
        @apply text-error-50;
      }
    }
  }
}
</style>
